import { Schema } from "effect"
import { EntityRef, NodeRef, Principal } from "./common"
import { Intake } from "./domain/intake"
import { Artifact, Comment, MemoryItem } from "./domain/knowledge"
import { Agent, Area, Policy } from "./domain/org"
import { Goal, Initiative, Kpi } from "./domain/plan"
import { Proposal } from "./domain/proposal"
import { Edge, Task, TaskStatus } from "./domain/task"
import { Member, Project } from "./domain/workspace"
import { AgentId, Epoch, Millis, NonNegInt, ProposalId, RunId, RunnerId, Seq, TaskId, UserId } from "./ids"
import { Unmet } from "./placement"
import { RunViewPhase } from "./rpc/run"
import { ActivityUsage, Run } from "./run"

// #region project-state
/**
 * The decision state of one project: canonical in memory for `decide`, encoded as the snapshot clients decode,
 * and rebuilt by folding the log from the nearest snapshot. History (resolved proposals beyond a window, ended
 * runs, comment bodies, KPI series, memory history) is served from SQL projections, not kept here.
 */
export class ProjectState extends Schema.Opaque<ProjectState>()(
  Schema.Struct({
    seq: Seq,
    planVersion: Schema.Int,
    project: Schema.NullOr(Project), // null before genesis
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
    policies: Schema.Array(Policy),
    areas: Schema.Array(Area),
    agents: Schema.Array(Agent), // roster: lead + role agents
    goals: Schema.Array(Goal),
    kpis: Schema.Array(Kpi),
    initiatives: Schema.Array(Initiative),
    tasks: Schema.Array(Task), // all, including closed ones (they are part of the plan)
    edges: Schema.Array(Edge),
    /** Open proposals plus those resolved within the compaction window (ghosts fade out on the canvas). */
    proposals: Schema.Array(Proposal),
    /** Runs not yet ended, plus the latest run per task and per agent. */
    runs: Schema.Array(Run),
    /** Open asks only (bodies included); other comments are fetched per thread. */
    openAsks: Schema.Array(Comment),
    artifacts: Schema.Array(Artifact), // headers
    memory: Schema.Array(MemoryItem), // accepted project, area and task items
    /** Open intake (new or triaged) plus items resolved within the compaction window. [P2] */
    intake: Schema.Array(Intake),
    /** Per-kind counters for short refs (G-, K-, I-, T-, P-, R-). Assigned in decide, carried in the event. */
    refCounters: Schema.Struct({
      G: Schema.Int,
      K: Schema.Int,
      I: Schema.Int,
      T: Schema.Int,
      P: Schema.Int,
      R: Schema.Int,
      /** Temporary teammates (settings wave §5.3): W-n workers, V-n validators. Absent = 0. */
      W: Schema.optionalKey(Schema.Int),
      V: Schema.optionalKey(Schema.Int),
    }),
  }),
) {}
// #endregion project-state

// #region views
/** Derived on server and client by the same core function. Never stored, never written by agents. */
export const Readiness = Schema.TaggedUnion({
  ready: {},
  not_applicable: { status: TaskStatus }, // done, cancelled, impossible, in_review, blocked
  running: {
    runId: RunId,
    state: Schema.Literals(["requested", "waiting", "leased"]),
    unmet: Schema.Array(Unmet),
  }, // "waiting for runner" lives here
  paused: {},
  unassigned: {},
  assignee_paused: { agentId: AgentId },
  needs_contract: {}, // planner rule: no ready task without ≥ 1 done check
  not_now: { initiativeStatus: Schema.String, horizon: Schema.String }, // initiative not active, or horizon ≠ now
  waiting_request: {}, // a cross-area request the receiving area has not accepted yet
  waiting_deps: { on: Schema.Array(NodeRef) }, // unfinished blocks-predecessors
  waiting_decision: { proposalIds: Schema.Array(ProposalId) }, // open proposals that list it in `blocks`
  at_capacity: { agentId: AgentId, limit: Schema.Int }, // the assignee's spawn_workers.maxConcurrent is reached
  retry_backoff: { until: Millis },
  /** Settings wave K8: the project is paused or archived; no new worker attempts. */
  project_inactive: { status: Schema.Literals(["paused", "archived"]) },
  /** Settings wave K10: the assignee's owner left the workspace; someone must take the agent over first. */
  assignee_owner_left: { agentId: AgentId },
})
export type Readiness = typeof Readiness.Type

/** Live agent state for avatars, rosters and canvas presence. Projection pushed by the Agent DO; never logged. */
export const AgentPresence = Schema.Struct({
  agentId: AgentId,
  state: Schema.Literals(["idle", "queued", "thinking", "working", "away", "paused", "needs_attention"]),
  reason: Schema.NullOr(Schema.String), // "vps-1 offline since 14:02", "claude profile logged out"
  currentRunId: Schema.NullOr(RunId),
  focus: Schema.Array(EntityRef), // what it is reading or editing right now
  activeWorkers: Schema.Int,
  queuedWakes: Schema.Int,
  lastActiveAt: Schema.NullOr(Millis),
})

/**
 * Ephemeral live activity of one run (fix wave §4.3, decision 0022): folded by the Run DO from the frames it stores
 * (core `foldActivity`), relayed by the Project DO in `SyncFrame.Presence.runs`. Never enters ProjectState, the log,
 * a pack or any agent tool. Carries no tool output and no reasoning content.
 */
export const RunActivity = Schema.Struct({
  runId: RunId,
  epoch: Epoch,
  /** Time of the newest content frame. */
  at: Millis,
  phase: RunViewPhase,
  /** From the last heartbeat: time since the CLI produced anything. */
  idleMs: NonNegInt,
  /** The effective model, once known. */
  model: Schema.NullOr(Schema.String),
  frames: NonNegInt,
  toolCalls: NonNegInt,
  last: Schema.NullOr(
    Schema.TaggedUnion({
      tool: {
        name: Schema.String,
        subject: Schema.NullOr(Schema.String),
        state: Schema.Literals(["running", "done", "error", "denied"]),
      },
      /** ≤ 140 chars, assistant text only. */
      text: { excerpt: Schema.String },
      /** No reasoning content, ever. */
      thinking: {},
      permission: { tool: Schema.String, title: Schema.String },
      /** ≤ 200 chars, from data-cli-error / turn.failed. */
      error: { message: Schema.String },
      milestone: { kind: Schema.String, text: Schema.String },
    }),
  ),
  usage: Schema.NullOr(ActivityUsage),
})
export type RunActivity = typeof RunActivity.Type

/** Ephemeral presence for people and agents, piggybacked on the project socket. */
export const PresenceEntry = Schema.Struct({ who: Principal, focus: Schema.Array(EntityRef), at: Millis })

/** Runner dot on the canvas and the Runners screen (derived from the hub's rows). */
export const RunnerStatus = Schema.Struct({
  runnerId: RunnerId,
  presence: Schema.Literals(["online", "busy", "away", "offline", "needs_attention"]),
  freeSlots: Schema.Int,
  leases: Schema.Array(Schema.Struct({ runId: RunId, epoch: Epoch, taskId: Schema.NullOr(TaskId) })),
  lastSeenAt: Schema.NullOr(Millis),
})
// #endregion views
